import * as Schema from "effect/Schema";
import { Rpc, RpcGroup } from "effect/rpc";
import { ContentHash, Dimensions, Identifier, Revision, SafeInteger, NonNegative, TimeMs, UnitInterval } from "@linempo/kernel";
import { AnnotationProject, AssetDescriptor, SubtitleCue } from "@linempo/project-domain";
import { AnimationGenerationResult, AnimationInspection, PreviewTarget, ArtifactDescriptor, ExportSettings, RenderError, transferBuffers, validateRenderBundle } from "@linempo/render-domain";
export const PROTOCOL_VERSION = 1;
export const BootstrapDescriptor = Schema.Struct({
    protocolVersion: Schema.Literal(PROTOCOL_VERSION),
    kind: Schema.Literals(["preview", "export"]),
});
export const JobContext = Schema.Struct({
    jobId: Identifier,
    projectId: Identifier,
    projectRevision: Revision,
});
const contextFields = JobContext.fields;
export const PrepareCommand = Schema.TaggedStruct("Prepare", {
    ...contextFields,
    project: AnnotationProject,
    target: Schema.optionalKey(PreviewTarget),
}).check(Schema.makeFilter((command) => command.project.id === command.projectId && command.project.revision === command.projectRevision || "job must match the project identity and revision"));
export const GenerateCommand = Schema.TaggedStruct("Generate", {
    ...contextFields, project: AnnotationProject, animationId: Identifier,
}).check(Schema.makeFilter((command) => command.project.id === command.projectId && command.project.revision === command.projectRevision || "job must match the project identity and revision"));
export const ExportCommand = Schema.TaggedStruct("Export", {
    ...contextFields,
    planId: Identifier,
    settings: ExportSettings,
});
export const SeekCommand = Schema.TaggedStruct("Seek", {
    ...contextFields,
    planId: Identifier,
    requestSequence: SafeInteger,
    timeMs: TimeMs,
    target: Dimensions.check(Schema.makeFilter((size) => Math.max(size.width, size.height) <= 1280 || "preview longest edge must be at most 1280")),
});
export const CancelCommand = Schema.TaggedStruct("Cancel", { ...contextFields });
export const WorkerCommand = Schema.Union([PrepareCommand, GenerateCommand, ExportCommand, SeekCommand, CancelCommand]);
export const Started = Schema.TaggedStruct("Started", { ...contextFields });
export const Progress = Schema.TaggedStruct("Progress", {
    ...contextFields,
    phase: Schema.Literals(["lookup", "decode", "mask", "skeleton", "trace", "persist", "plan", "probe", "render", "flush", "finalize"]),
    elapsedMs: Schema.optionalKey(NonNegative),
    fraction: UnitInterval,
    completedFrames: SafeInteger,
    totalFrames: SafeInteger,
    queuedFrames: SafeInteger,
    queuedBytes: SafeInteger,
}).check(Schema.makeFilter((event) => event.completedFrames <= event.totalFrames || "progress exceeds total frame count"));
export const Prepared = Schema.TaggedStruct("Prepared", {
    ...contextFields,
    planId: Identifier,
    durationMs: TimeMs,
    pathCount: SafeInteger,
    target: Schema.optionalKey(PreviewTarget),
});
export const Generated = Schema.TaggedStruct("Generated", { ...contextFields, result: AnimationGenerationResult });
export const Completed = Schema.TaggedStruct("Completed", {
    ...contextFields,
    artifact: ArtifactDescriptor,
}).check(Schema.makeFilter((event) => event.artifact.jobId === event.jobId && event.artifact.projectRevision === event.projectRevision || "artifact identity differs from completed job"));
export const Failed = Schema.TaggedStruct("Failed", { ...contextFields, error: RenderError });
export const CancelledEvent = Schema.TaggedStruct("Cancelled", { ...contextFields });
export const RenderEvent = Schema.Union([Started, Progress, Prepared, Generated, Completed, Failed, CancelledEvent]);
export const PreviewFrameDescriptor = Schema.Struct({
    ...contextFields,
    planId: Identifier,
    requestSequence: SafeInteger,
    timeMs: TimeMs,
    dimensions: Dimensions,
    transferId: Identifier,
});
/** ImageBitmap/ArrayBuffer objects travel on a separate structured-clone channel. */
export const TransferDescriptor = Schema.Struct({
    protocolVersion: Schema.Literal(PROTOCOL_VERSION),
    transferId: Identifier,
    ...contextFields,
    planId: Identifier,
    ownership: Schema.Literal("receiver"),
    buffers: Schema.Array(Schema.Struct({
        id: Identifier,
        kind: Schema.Literals(["path-f64", "raster-rgba8", "mask-alpha8"]),
        byteLength: SafeInteger,
    })),
}).check(Schema.makeFilter((descriptor) => new Set(descriptor.buffers.map((buffer) => buffer.id)).size === descriptor.buffers.length || "duplicate transferred buffer ID"));
export function describeBundleTransfer(context, transferId, bundle) {
    validateRenderBundle(bundle);
    if (context.projectId !== bundle.plan.projectId || context.projectRevision !== bundle.plan.projectRevision) {
        throw new TypeError("transfer context does not match plan");
    }
    return Schema.decodeUnknownSync(TransferDescriptor)({
        protocolVersion: PROTOCOL_VERSION, transferId, ...context, planId: bundle.plan.id, ownership: "receiver",
        buffers: [
            ...[...bundle.pathBuffers].map(([id, data]) => ({ id, kind: "path-f64", byteLength: data.byteLength })),
            ...[...bundle.rasterBuffers].map(([id, data]) => ({ id, kind: "raster-rgba8", byteLength: data.byteLength })),
            ...[...bundle.maskBuffers].map(([id, data]) => ({ id, kind: "mask-alpha8", byteLength: data.byteLength })),
        ],
    });
}
/** Caller transfers buffers in postMessage; cloneRenderBundle first if retaining the source. */
export function createBundleTransfer(context, transferId, bundle) {
    return { descriptor: describeBundleTransfer(context, transferId, bundle), buffers: transferBuffers(bundle) };
}
/** Load the separately stored plan by descriptor.planId before accepting its binary payload. */
export function receiveBundleTransfer(plan, input) {
    const descriptor = Schema.decodeUnknownSync(TransferDescriptor)(input.descriptor);
    if (descriptor.planId !== plan.id || descriptor.projectId !== plan.projectId || descriptor.projectRevision !== plan.projectRevision) {
        throw new TypeError("transferred bundle does not match plan identity");
    }
    if (descriptor.buffers.length !== input.buffers.length)
        throw new TypeError("transfer buffer count mismatch");
    const pathBuffers = new Map();
    const rasterBuffers = new Map();
    const maskBuffers = new Map();
    descriptor.buffers.forEach((meta, index) => {
        const buffer = input.buffers[index];
        if (!(buffer instanceof ArrayBuffer) || buffer.byteLength !== meta.byteLength)
            throw new TypeError("transfer buffer size mismatch");
        if (meta.kind === "path-f64") {
            if (buffer.byteLength % Float64Array.BYTES_PER_ELEMENT !== 0)
                throw new TypeError("invalid f64 buffer alignment");
            pathBuffers.set(meta.id, new Float64Array(buffer));
        }
        else if (meta.kind === "raster-rgba8")
            rasterBuffers.set(meta.id, new Uint8Array(buffer));
        else
            maskBuffers.set(meta.id, new Uint8Array(buffer));
    });
    return validateRenderBundle({ plan, pathBuffers, rasterBuffers, maskBuffers });
}
/** The streamed request owns the job scope; client stream interruption is RPC Interrupt. */
export const PrepareRpc = Rpc.make("Prepare", { payload: PrepareCommand, success: RenderEvent, error: RenderError, stream: true });
export const GenerateRpc = Rpc.make("Generate", { payload: GenerateCommand, success: RenderEvent, error: RenderError, stream: true });
export const ExportRpc = Rpc.make("Export", { payload: ExportCommand, success: RenderEvent, error: RenderError, stream: true });
export const SeekRpc = Rpc.make("Seek", { payload: SeekCommand, success: PreviewFrameDescriptor, error: RenderError });
export const CancelRpc = Rpc.make("Cancel", { payload: CancelCommand, success: Schema.Void, error: RenderError });
export const PingRpc = Rpc.make("Ping", {
    payload: Schema.Struct({ protocolVersion: Schema.Literal(PROTOCOL_VERSION) }),
    success: Schema.Struct({ protocolVersion: Schema.Literal(PROTOCOL_VERSION), activeJobId: Schema.NullOr(Identifier) }),
    error: RenderError,
});
export const WorkerRpcs = RpcGroup.make(PrepareRpc, GenerateRpc, ExportRpc, SeekRpc, CancelRpc, PingRpc);
/** Workspace operations run only in the preview Worker. Files use framePort, not RPC JSON. */
export const MAX_ASSET_FILE_BYTES = 32 * 1024 * 1024;
/** Raw input is normalized in the workspace Worker before becoming a stored asset. */
export const MAX_ASSET_INPUT_BYTES = 64 * 1024 * 1024;
export const MAX_SUBTITLE_CHARACTERS = 2 * 1024 * 1024;
export const NativeFileDescriptor = Schema.Struct({
    protocolVersion: Schema.Literal(PROTOCOL_VERSION),
    kind: Schema.Literals(["asset-input", "asset-output", "artifact-output"]),
    transferId: Identifier,
    byteLength: SafeInteger.check(Schema.isGreaterThan(0)),
});
export const ParseSubtitleRpc = Rpc.make("ParseSubtitle", {
    payload: Schema.Struct({ source: Schema.String.check(Schema.isMaxLength(MAX_SUBTITLE_CHARACTERS)) }),
    success: Schema.Struct({ cues: Schema.Array(SubtitleCue), sourceHash: ContentHash }),
    error: RenderError,
});
export const LoadProjectRpc = Rpc.make("LoadProject", {
    payload: Schema.Struct({ projectId: Identifier }), success: Schema.NullOr(AnnotationProject), error: RenderError,
});
export const SaveProjectRpc = Rpc.make("SaveProject", {
    payload: Schema.Struct({ project: AnnotationProject, expectedRevision: Schema.NullOr(Revision) }),
    success: Schema.Void, error: RenderError,
});
export const ImportAssetRpc = Rpc.make("ImportAsset", {
    payload: Schema.Struct({ transferId: Identifier, assetId: Identifier }),
    success: AssetDescriptor, error: RenderError,
});
/** Source image delivery is for the annotation editor, still bounded by MAX_ASSET_FILE_BYTES. */
export const ReadAssetRpc = Rpc.make("ReadAsset", {
    payload: Schema.Struct({ asset: AssetDescriptor, transferId: Identifier, thumbnail: Schema.optional(Schema.Boolean) }),
    success: NativeFileDescriptor, error: RenderError,
});
export const ReadArtifactRpc = Rpc.make("ReadArtifact", {
    payload: Schema.Struct({ artifactId: Identifier, transferId: Identifier }),
    success: Schema.Struct({ artifact: ArtifactDescriptor, file: NativeFileDescriptor }), error: RenderError,
});
export const RemoveArtifactRpc = Rpc.make("RemoveArtifact", {
    payload: Schema.Struct({ artifactId: Identifier }), success: Schema.Void, error: RenderError,
});
export const InspectAnimationsRpc = Rpc.make("InspectAnimations", {
    payload: Schema.Struct({ project: AnnotationProject, animationIds: Schema.Array(Identifier) }),
    success: Schema.Array(AnimationInspection), error: RenderError,
});
export const WorkspaceRpcs = RpcGroup.make(InspectAnimationsRpc, ParseSubtitleRpc, LoadProjectRpc, SaveProjectRpc, ImportAssetRpc, ReadAssetRpc, ReadArtifactRpc, RemoveArtifactRpc);
export const ClientRpcs = WorkerRpcs.merge(WorkspaceRpcs);
export const isTerminalEvent = (event) => event._tag === "Prepared" || event._tag === "Generated" || event._tag === "Completed" || event._tag === "Failed" || event._tag === "Cancelled";
export function acceptJobEvent(state, event) {
    if (state.status === "terminal" || state.context.jobId !== event.jobId
        || state.context.projectId !== event.projectId || state.context.projectRevision !== event.projectRevision)
        return state;
    return { context: state.context, status: isTerminalEvent(event) ? "terminal" : "running", lastEvent: event };
}
/** Single-slot preview consumers close rejected/replaced bitmaps in their adapter. */
export function acceptsPreviewFrame(expected, frame) {
    return expected.jobId === frame.jobId && expected.projectId === frame.projectId
        && expected.projectRevision === frame.projectRevision && expected.planId === frame.planId
        && expected.requestSequence === frame.requestSequence;
}
